import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { formatDate, statusClass } from "@/lib/format";
import { ArrowLeft, Building2, Receipt, FileText, Sparkles } from "lucide-react";

export default function ClientDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get(`/clients/${id}`)
      .then((r) => setClient(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, [id]);

  if (err) return (
    <div className="border border-red-900/60 bg-red-950/20 px-4 py-3 font-mono text-xs text-red-300">{err}</div>
  );
  if (!client) return <div className="font-mono text-xs text-neutral-500"><span className="cursor-blink">LOADING</span></div>;

  return (
    <div data-testid={TID.clientDetail} className="space-y-6">
      <Link
        to="/app/clients"
        className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-neutral-500 hover:text-[#EDEDED]"
      >
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
        Torna a Clienti
      </Link>

      <section className="border border-[#1F1F1F] bg-[#0C0C0C]">
        <header className="border-b border-[#1F1F1F] px-5 py-4 flex items-start justify-between bg-[#141414]">
          <div>
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500 flex items-center gap-2">
              <Building2 className="w-3 h-3 text-[#0055FF]" strokeWidth={1.5} />
              cliente · {client.id}
            </div>
            <h2 className="font-heading uppercase text-3xl tracking-tight mt-1">{client.name}</h2>
            <div className="font-mono text-xs text-neutral-500 mt-1">{client.sector}</div>
          </div>
          <span className={`font-mono text-[10px] uppercase tracking-widest border px-2 py-1 ${statusClass(client.status)}`}>
            {client.status}
          </span>
        </header>

        <div className="px-5 py-4 border-b border-[#1F1F1F] font-mono text-[11px] text-neutral-500 flex flex-wrap gap-x-6 gap-y-1">
          <div>cliente da · <span className="text-neutral-300">{formatDate(client.since)}</span></div>
          <div>owner · <span className="text-neutral-300">{client.owner_email}</span></div>
        </div>

        {client.notes && (
          <div className="px-5 py-4">
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500 mb-1">Note</div>
            <p className="text-sm text-neutral-300">{client.notes}</p>
          </div>
        )}
      </section>

      <section className="border border-[#1F1F1F] bg-[#0C0C0C]">
        <header className="border-b border-[#1F1F1F] px-4 py-3 bg-[#141414] flex items-center justify-between">
          <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-400">
            Progetti
          </span>
          <span className="font-mono text-[10px] text-neutral-600">{client.projects.length}</span>
        </header>
        <ul className="divide-y divide-[#1F1F1F]">
          {client.projects.map((p) => (
            <li key={p.id} data-testid={TID.projectItem} className="px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[10px] text-neutral-500">{p.id}</div>
                  <h3 className="text-lg text-[#EDEDED] mt-0.5">{p.name}</h3>
                  <div className="font-mono text-xs text-neutral-500 mt-1">
                    corrente · <span className="text-[#0055FF]">{p.milestone_current}</span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1 bg-[#1F1F1F] max-w-xs">
                      <div
                        className="h-full bg-[#EDEDED]"
                        style={{ width: `${(p.milestones_done / p.milestones_total) * 100}%` }}
                      />
                    </div>
                    <span className="font-mono text-[10px] text-neutral-500">
                      {p.milestones_done}/{p.milestones_total}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${statusClass(p.status)}`}>
                    {p.status}
                  </span>
                  <div className="font-mono text-[10px] text-neutral-500">
                    start · {formatDate(p.started_at)}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {p.lead_id && (
                  <ArtifactPill to={`/app/leads/${p.lead_id}`} icon={Sparkles} label={`Lead ${p.lead_id}`} />
                )}
                {p.quote_id && (
                  <ArtifactPill to={`/app/quotes/${p.quote_id}`} icon={Receipt} label={`Preventivo ${p.quote_id}`} />
                )}
                {p.prd_id && (
                  <ArtifactPill to={`/app/prds/${p.prd_id}`} icon={FileText} label={`PRD ${p.prd_id}`} />
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function ArtifactPill({ to, icon: Icon, label }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 border border-[#1F1F1F] hover:border-[#333] hover:bg-[#141414] px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-neutral-400 hover:text-[#EDEDED] transition-colors"
    >
      <Icon className="w-3 h-3" strokeWidth={1.5} />
      {label}
    </Link>
  );
}
